import {
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from "@chakra-ui/react";
import { ChevronDownIcon, RepeatIcon } from "@chakra-ui/icons";
import { CiSearch } from "react-icons/ci";
import { useMemo } from "react";
import {
  REDIS_SOLICITACAO_STATUSES,
  REDIS_STATUS_LABELS,
  RedisSolicitacaoStatus,
} from "@/models/auction-redis.model";

interface ProgressFiltersShape<S extends string> {
  searchTerm: string;
  statuses: S[];
  codigoLote: string;
}

interface CreditAuctionProgressFiltersProps<S extends string> {
  filters: ProgressFiltersShape<S>;
  onChange: (next: ProgressFiltersShape<S>) => void;
  onApply: () => void;
  onClear: () => void;
  onRefresh?: () => void;
  /** Opções do menu de status; default = máquina de status do Redis. */
  statusOptions?: readonly S[];
  statusLabels?: Record<S, string>;
}

/**
 * Filtros das abas de tabela do Leilão CLT ("Progresso leilão" e "Histórico
 * leilão") — projetado para ser renderizado dentro da barra colorida unificada
 * (sem container próprio). Genérico no conjunto de status (o histórico tem
 * NO_BID além dos status do Redis).
 */
export default function CreditAuctionProgressFilters<
  S extends string = RedisSolicitacaoStatus,
>({
  filters,
  onChange,
  onApply,
  onClear,
  onRefresh,
  statusOptions = REDIS_SOLICITACAO_STATUSES as unknown as readonly S[],
  statusLabels = REDIS_STATUS_LABELS as Record<S, string>,
}: CreditAuctionProgressFiltersProps<S>) {
  const statusButtonLabel = useMemo(() => {
    if (filters.statuses.length === 0) return "Todos os status";
    if (filters.statuses.length === 1) {
      return statusLabels[filters.statuses[0]];
    }
    return `${filters.statuses.length} status selecionados`;
  }, [filters.statuses, statusLabels]);

  return (
    <Flex
      gap={3}
      alignItems="center"
      flexWrap="wrap"
      flexDirection={{ base: "column", md: "row" }}
    >
      <InputGroup width={{ base: "100%", md: "260px" }} flexShrink={0}>
        <InputLeftElement
          pointerEvents="none"
          h={{ base: "56px", md: "48px" }}
        >
          <CiSearch color="gray" size="1.25rem" />
        </InputLeftElement>
        <Input
          placeholder="CPF ou nº da solicitação"
          variant="filled"
          height={{ base: "56px", md: "48px" }}
          type="text"
          bg="white"
          maxLength={30}
          value={filters.searchTerm}
          onChange={(ev) =>
            onChange({ ...filters, searchTerm: ev.target.value })
          }
          color="black"
          _focus={{ bg: "white" }}
          _hover={{ bg: "white" }}
          fontFamily="Lexend Deca"
          fontSize={{ base: "xs", md: "sm" }}
        />
      </InputGroup>

      <Menu closeOnSelect={false}>
        <MenuButton
          as={Button}
          rightIcon={<ChevronDownIcon />}
          bg="white"
          color="black"
          _hover={{ bg: "white" }}
          _active={{ bg: "white" }}
          h={{ base: "56px", md: "48px" }}
          minW="260px"
          fontFamily="Lexend Deca"
          fontWeight="normal"
          fontSize={{ base: "xs", md: "sm" }}
          textAlign="left"
        >
          {statusButtonLabel}
        </MenuButton>
        <MenuList minW="260px" zIndex={1500} color="black">
          <MenuOptionGroup
            type="checkbox"
            value={filters.statuses}
            onChange={(value) =>
              onChange({
                ...filters,
                statuses: (Array.isArray(value) ? value : [value]) as S[],
              })
            }
          >
            {statusOptions.map((status) => (
              <MenuItemOption key={status} value={status}>
                {statusLabels[status]}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Menu>

      <InputGroup width={{ base: "100%", md: "220px" }} flexShrink={0}>
        <Input
          placeholder="Código do lote"
          variant="filled"
          height={{ base: "56px", md: "48px" }}
          type="text"
          bg="white"
          maxLength={60}
          value={filters.codigoLote}
          onChange={(ev) => onChange({ ...filters, codigoLote: ev.target.value })}
          color="black"
          _focus={{ bg: "white" }}
          _hover={{ bg: "white" }}
          fontFamily="Lexend Deca"
          fontSize={{ base: "xs", md: "sm" }}
        />
      </InputGroup>

      <HStack spacing={2}>
        <Button
          size="md"
          h={{ base: "56px", md: "48px" }}
          bg="white"
          color="teal.700"
          _hover={{ bg: "whiteAlpha.900" }}
          onClick={onApply}
          fontFamily="Lexend Deca"
          fontSize={{ base: "xs", md: "sm" }}
        >
          Filtrar
        </Button>
        <Button
          size="md"
          h={{ base: "56px", md: "48px" }}
          variant="outline"
          color="white"
          borderColor="whiteAlpha.700"
          _hover={{ bg: "whiteAlpha.200" }}
          onClick={onClear}
          fontFamily="Lexend Deca"
          fontSize={{ base: "xs", md: "sm" }}
        >
          Limpar
        </Button>
        {onRefresh && (
          <Button
            size="md"
            h={{ base: "56px", md: "48px" }}
            variant="outline"
            color="white"
            borderColor="whiteAlpha.700"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={onRefresh}
            fontFamily="Lexend Deca"
            fontSize={{ base: "xs", md: "sm" }}
            leftIcon={<RepeatIcon />}
          >
            Atualizar
          </Button>
        )}
      </HStack>
    </Flex>
  );
}
